import {
    AddFlow,
    flowSpcice,
    gaCommonEvent,
    gaLoadedPromise,
    productClick,
    productDetailBrowse,
    productsCheckoutProgress,
} from "@/src/utils";
import axon from "./analysis/axon";
import { getCookieItem, jointImg, setLocalItem } from "@/lib/commonService";
import Kwai from "./analysis/KwaiPixel";
import pintrkPixel from "./analysis/PintrkPixel";
import newTiktokPixel from "./analysis/newTiktokPixel";
import { CURRENCY_COOKIE_NAME } from "@/config/constConf";
import {
    personalizeBatchEventLogin,
    personalizeRegister,
} from "@/src/models/personalize";
import { subscription } from "@/src/models/loginModel";
import reddit from "./analysis/reddit";
import { bindTouristId } from "@/src/models";
import { registerEventChannel } from "@/src/models/CPUChannelData";
import {
    ColorItem,
    IInitedInfo,
    IProductDetailsInfo,
} from "@/store/modules/productDetails";
import { GoogleAnalytics, facebookPixel } from "./utils";
import criteoAd from "./analysis/criteo";
import cybertargeter from "./analysis/Cybertargeter";
import {
    addClickPayEvent,
    addProductsToDetailEvent,
} from "@/src/models/emailData";
import addFlow from "@/src/utils/flow";
import acquisition from "./analysis/DataAcquisition";
import {
    ProductClickParams,
    IGlassProduct,
    IGAStaticParams,
    ITikTokContentItem,
    ICreateOrder,
    ITikTokEventData,
} from "./commonEventType";

/**
 * 登录事件
 * @param data
 */
export function loginEvent(data: any) {
    gaCommonEvent("login");
    axon.trackUserLogin(data.customersId);
}

/**
 * 注册事件
 * @param data 响应
 * @param params 请求参数
 */
export function signUpEvent(data: any, params: any) {
    AddFlow.register(data.customers_id);
    flowSpcice.register(data.customers_ids);
    reddit.SignUp();
    setLocalItem("email", params.email_address);
    bindTouristId();
    registerEventChannel();
    window.fbq &&
        window.fbq("track", "CompleteRegistration", {
            value: 1.0,
            currency: getCookieItem(CURRENCY_COOKIE_NAME),
        });
    newTiktokPixel.CompleteRegistration();
    Kwai.completeRegistration();
    pintrkPixel.Signup();
    gaCommonEvent("sign_up");
    personalizeRegister(params.email_address);
    personalizeBatchEventLogin(data.customer_id);
    axon.trackUserSignUp({ user_id: data.customer_id, method: "email" });
    subscription(params.telparms);
}

/**
 * 商品详情页的浏览事件
 * @param productDetail 商品详情
 * @param activeObj 当前浏览的商品
 * @returns void
 */
export function viewContentEvent(
    productDetail: Partial<IInitedInfo & IProductDetailsInfo>,
    activeObj: Partial<ColorItem>,
) {
    const { onsale, products_id, id: color_id, color_image } = activeObj;
    if (!onsale || !products_id) return;
    const site = window.getSite();
    const content_id =
        site === "uk" ? products_id + "-" + activeObj.id : products_id; // activeObj.id即为color_id
    const content_type = "product";
    const currency = getCookieItem(CURRENCY_COOKIE_NAME) || "USD";

    facebookPixel({
        trackPage: "ViewContent",
        trackPageContent: {
            content_ids: [content_id], //产品id 数组
            content_type, // "product"
            price: onsale?.price, //产品价格 不带货币单位
            currency, //价格货币单位
        },
    });

    criteoAd.viewItem(activeObj.id);

    GoogleAnalytics(
        {
            ecomm_prodid: products_id, //产品ID//填充
            ecomm_pagetype: content_type,
            ecomm_totalvalue: Number(onsale?.price), //产品价格//填充
        },
        () => {
            productDetailBrowse({
                currency: getCookieItem(CURRENCY_COOKIE_NAME),
                value: activeObj?.onsale?.price,
                items: [
                    {
                        id: productDetail.products_id,
                        name: productDetail.products_model,
                        item_id: productDetail.products_id,
                        item_name: productDetail.products_model,
                        price: onsale?.price,
                        item_variant_id: color_id,
                        item_category_id: 524,
                        image_url: jointImg({
                            productId: products_id,
                            size: "middle",
                            imgTotal: 2,
                            name: color_image || "",
                        }),
                    },
                ],
            });
        },
    );

    reddit.ViewContent();

    newTiktokPixel.ViewContent(
        {
            content_type: "product",
            contents: [
                {
                    quantity: 1,
                    content_id: `${products_id}`,
                    price: onsale?.price,
                },
            ],
            currency: currency,
            value: onsale?.price,
        },
        products_id,
    );

    // 快手
    Kwai.contentView({
        content_type: "product",
        contents: [
            {
                quantity: 1,
                content_id: `${products_id}`,
                price: Number(onsale?.price),
            },
        ],
        currency: currency,
        value: Number(onsale?.price),
    });

    cybertargeter.ViewContent({
        value: Number(onsale?.price),
        currency: currency,
        content_name: productDetail.products_model,
        content_type: "product",
        content_ids: [products_id],
        contents: [
            {
                quantity: 1,
                id: `${products_id}`,
                price_amount: onsale.price,
            },
        ],
    });
}

// ========== 产品点击相关函数 ==========

/**
 * 通用产品点击事件
 * 整合多个平台的产品点击追踪
 * @param params 产品点击参数
 */
export const productClickCommon = ({
    products_id,
    products_model,
    price,
    filterItem,
    filterSubItem,
    colorId,
    countPrice,
    email,
    domain,
}: ProductClickParams): void => {
    const quantity = 1;
    const currency = getCookieItem(CURRENCY_COOKIE_NAME);

    // Flow追踪
    addFlow.ProductClick(products_id);
    flowSpcice.ProductClick(products_id);

    // 快手追踪
    Kwai.buttonClick({
        content_id: products_id,
        content_name: products_model,
        price,
        quantity,
        currency,
    });

    // 邮件营销数据追踪
    addProductsToDetailEvent(
        {
            colorId: colorId,
            price: countPrice || price,
            filterItem,
            filterSubItem,
        },
        domain ? { domain, email } : {},
    );

    // Google Analytics产品点击追踪
    productClick({
        currency,
        value: price,
        items: [
            {
                id: products_id,
                name: products_model,
                item_id: products_id,
                item_name: products_model,
            },
        ],
    });
};

// ========== 订单相关函数 ==========

/**
 * 构建Google Analytics统计参数
 * @param products 眼镜产品列表
 * @param payment_module_code 支付模块代码
 * @returns GA统计参数对象
 */
export function getGAStaticParams(
    products: IGlassProduct[],
    payment_module_code: string,
): IGAStaticParams {
    const result: IGAStaticParams = {
        items: [],
        event_label: payment_module_code,
        content_type: payment_module_code,
    };

    // 验证产品数据有效性
    if (!products || !products.length) {
        console.warn("构建GA参数时产品列表为空");
        return result;
    }

    // 转换产品数据为GA格式
    const items = products.map((item) => {
        const { product_id, frame, quantity, price } = item;
        return {
            id: product_id,
            item_id: product_id,
            quantity: quantity || 1,
            price: price || 0,
            name: frame || `Product-${product_id}`,
            item_name: frame || `Product-${product_id}`,
        };
    });

    result.items = items;
    return result;
}

/**
 * 构建TikTok追踪所需的Contents数组
 * @param gaItems GA格式的商品数组
 * @returns TikTok Contents数组
 */
export function buildTikTokContents(gaItems: any[]): ITikTokContentItem[] {
    if (!gaItems || !gaItems.length) {
        return [];
    }

    return gaItems.map((element) => ({
        quantity: element.quantity || 1,
        content_id: element.id,
        price: element.price || 0,
        content_name: element.name || "",
    }));
}

/**
 * 计算订单中的产品总数量
 * @param contents TikTok Contents数组
 * @returns 产品总数量
 */
function calculateProductQuantity(contents: ITikTokContentItem[]): number {
    if (!contents || !contents.length) {
        return 0;
    }

    return contents.reduce((accumulator, current) => {
        const quantity =
            typeof current.quantity === "number" ? current.quantity : 1;
        return accumulator + quantity;
    }, 0);
}

/**
 * 订单创建事件处理函数
 * 整合所有订单创建相关的追踪事件，包括数据采集、GA统计、TikTok追踪、邮件营销等
 * @param orderData 订单数据对象，符合ICreateOrder接口规范
 */
export function createOrderEvent(orderData: ICreateOrder): void {
    // 服务端渲染环境检查
    if (typeof window === "undefined") return;

    try {
        // ========== 1. 数据验证和预处理 ==========
        const { orders, products } = orderData;
        const { glass: glassList } = products;

        // 验证必要数据
        if (!orders || !products) {
            console.error("订单数据或产品数据缺失");
            return;
        }

        const total = (products.total && products.total.price) || 0;
        const currency = orders.currency || "USD";

        // ========== 2. 数据采集追踪 (Acquisition) ==========
        try {
            if (orders.orders_oid && glassList && glassList.length > 0) {
                const productItems = glassList.map(
                    (item) => `${item.product_id}-${item.color_id}`,
                );
                acquisition.createOrder(
                    "createOrder",
                    productItems,
                    orders.orders_oid,
                );
            }
        } catch (error) {
            console.error("数据采集追踪失败:", error);
        }

        // ========== 3. Google Analytics 事件追踪 ==========
        const sendData = getGAStaticParams(
            glassList || [],
            orders.payment_module_code,
        );

        gaLoadedPromise()
            .then(() => {
                try {
                    if (sendData.items.length > 0) {
                        productsCheckoutProgress({
                            ...sendData,
                            currency: currency,
                            value: total,
                        });
                    }
                } catch (error) {
                    console.error("GA事件处理失败:", error);
                }
            })
            .catch((error) => {
                console.error("GA加载失败:", error);
            });

        // ========== 4. TikTok 像素追踪 ==========
        try {
            const contents = buildTikTokContents(sendData.items);
            const tiktokEventData: ITikTokEventData = {
                content_type: "product",
                contents: contents,
                currency: currency,
                value: total,
            };

            // 全球TikTok追踪
            newTiktokPixel.PlaceAnOrder(tiktokEventData);

            // 意大利站点专用TikTok追踪
        } catch (error) {
            console.error("TikTok追踪失败:", error);
        }

        // ========== 5. 邮件营销和用户行为追踪 ==========
        try {
            const contents = buildTikTokContents(sendData.items);
            const productQuantity = calculateProductQuantity(contents);

            addClickPayEvent({}, { productQuantity });
        } catch (error) {
            console.error("邮件营销追踪失败:", error);
        }
    } catch (error) {
        console.error("createOrderEvent执行失败:", error);
        // 确保错误不会阻断主流程
    }
}
